import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowBack,
  DirectionsBus,
  CurrencyRupee,
  Close,
  ExpandMore,
  Luggage,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

import { useNavigate, useParams } from "react-router-dom";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { useToast } from "../components/Toast";
import axios from "axios";
import { Loading } from "../components/Loading";
import BasicVehicleDetailsView from "../components/BasicVehicleDetailsView";
import FileUploadSection, {
  UploadedFile,
} from "../components/FileUploadSection";
import { LocalUser } from "../utils/UploadFile";
import { uploadFile } from "../utils/UploadFile";
import { fetchAllDocuments } from "./trip/add-trip/AddProof";
import { getValidToken } from "../utils/authUtils";

interface TripDetails {
  id: string;
  tripNumber: string;
  vehicleId: string;
  operatorId: string;
  source: string;
  destination: string;
  startDate: string;
  endDate: string;
  startOdometer: string;
  endOdometer: string | null;
  routeType: string;
  status: string;
  totalRevenue: number | null;
  totalPassengers: number | null;
  paymentType: string;
  operatorAmount: number | null;
  companyAmount: number | null;
  fuelCost: number | null;
  tollCharges: number | null;
  otherExpenses: number | null;
  notes: string | null;
  attachments: any[];
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
  journeyType: string;
  vehicle: {
    vehicleNumber: string;
    vehicleType: string;
    model: string;
    color: string;
    bodyType: string;
    configuration: string;
    fuelType: string;
    seatingCapacity: number;
    isAC: boolean;
    makeYear: number;
    acquisitionType: string;
    status: string;
  };
  serviceOperator: any | null;
  tripType: string;
  fromLocation: string;
  toLocation: string;
  journeyStartDate: string;
  journeyReturnDate: string;
  vehicleOdometerStart: string;
  vehicleOdometerEnd: string;
  advancePayment?: number;
}

interface TripIncomeSummary {
  totalIncome: number;
  totalAdvancePayment: number;
  totalCommission: number;
  busBookingCount: number;
  luggageBookingCount: number;
  startLuggageBookingCount: number;
  returnLuggageBookingCount: number;
  byType: {
    [key: string]: number;
  };
  byPaymentMethod: {
    [key: string]: number;
  };
  balanceAmount: number;
  totalBookings: number;
}

interface TripIncomes {
  incomes: any[];
  busIncomes: any[];
  luggageIncomes: any[];
  summary: TripIncomeSummary;
}

interface ExpenseSummary {
  totalExpense: number;
  totalPaid: number;
  balanceAmount: number;
  byType: {
    [key: string]: number;
  };
}

interface Expenses {
  expenses: any[];
  summary: ExpenseSummary;
}

interface LuggageJourneySectionProps {
  journeyType: "START" | "RETURN";
  incomes: any[];
  fromLocation: string;
  toLocation: string;
}

const LuggageJourneySection = ({
  journeyType,
  incomes,
  fromLocation,
  toLocation,
}: LuggageJourneySectionProps) => {
  const filteredIncomes = incomes
    .filter((income) => income.journeyType === journeyType)
    .sort((a, b) => {
      if (a.bookingMode === "INCLUDED") return -1;
      if (b.bookingMode === "INCLUDED") return 1;
      return 0;
    });
  const isStart = journeyType === "START";
  const title = isStart
    ? `Start Journey - ${fromLocation} to ${toLocation}`
    : `Return Journey - ${toLocation} to ${fromLocation}`;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="subtitle1"
        color="primary"
        sx={{ fontWeight: "medium", mb: 2 }}
      >
        {title}
      </Typography>
      {filteredIncomes.length > 0 ? (
        filteredIncomes.map((income, idx) => (
          <Paper
            sx={{ p: 2, mb: 3 }}
            key={`${journeyType.toLowerCase()}-${idx}`}
          >
            {income.bookingMode === "INCLUDED" && (
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <CheckCircleIcon sx={{ color: "green", mr: 1 }} /> Booking
                Included in Bus Booking
              </Typography>
            )}
            {income.bookingMode !== "COMMISSION" &&
              income.bookingMode !== "PICKUP" &&
              income.bookingMode !== "INCLUDED" && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Booking Type
                    </Typography>
                    <Typography variant="body1">
                      {income.bookingMode || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Operator Type
                    </Typography>
                    <Typography variant="body1">
                      {income.operatorType || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Bus Operator Name
                    </Typography>
                    <Typography variant="body1">
                      {income.operatorName || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Operator Contact Name
                    </Typography>
                    <Typography variant="body1">
                      {income.operatorContactName || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact Number
                    </Typography>
                    <Typography variant="body1">
                      {income.operatorContactNumber || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {income.bookingMode === "CONTRACT"
                        ? "Contract Amount"
                        : "Commission Amount"}
                    </Typography>
                    <Typography variant="body1">
                      {income.contractAmount || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Remark
                    </Typography>
                    <Typography variant="body1">
                      {income.remark || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Received Advance Payment
                    </Typography>
                    <Typography variant="body1">
                      {income.receivedAdvancePayment ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            {income.paymentDetails && income.paymentDetails.length > 0 && (
              <Box sx={{ mt: 3, p: 2, borderRadius: 1, bgcolor: "#f5f5f5" }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Advance Payment Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Received From
                    </Typography>
                    <Typography variant="body1">
                      {income.paymentDetails[0].paymentReceivedFrom || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Mode
                    </Typography>
                    <Typography variant="body1">
                      {income.paymentDetails[0].paymentMode || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Amount
                    </Typography>
                    <Typography variant="body1">
                      {income.paymentDetails[0].paymentAmount || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Date
                    </Typography>
                    <Typography variant="body1">
                      {income.paymentDetails[0].paymentDate || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Location
                    </Typography>
                    <Typography variant="body1">
                      {income.paymentDetails[0].paymentLocation || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Remarks
                    </Typography>
                    <Typography variant="body1">
                      {income.paymentDetails[0].remarks || "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
            {income.commissionBookings?.length > 0 && (
              <Box sx={{ mt: 3, p: 2, borderRadius: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  {income.bookingMode} Bookings
                </Typography>
                <CommissionBookingTable bookings={income.commissionBookings} />
              </Box>
            )}
            {/* <Box sx={{ mt: 3, bgcolor: "#f5f5f5", p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Balance Remaining
              </Typography>
              <Typography variant="body1">
                {income.balanceRemaining || "0"}
              </Typography>
            </Box> */}
          </Paper>
        ))
      ) : (
        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
          No {journeyType.toLowerCase()} journey luggage bookings found
        </Typography>
      )}
    </Box>
  );
};

interface CommissionBookingTableProps {
  bookings: LuggageBooking[];
}

interface LuggageBooking {
  id: string;
  lrNo: string;
  partyName: string;
  quantity: number;
  rate: number;
  tax: number;
  toPay: number;
  paid: number;
  amount: number;
  dropLocation?: string;
  luggageOperator?: string;
  luggageOperatorContactNumber?: string;
  luggageOperatorPricingType?: string;
  luggageOperatorPricingValue?: string;
  remarks?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

const CommissionBookingTable: React.FC<CommissionBookingTableProps> = ({
  bookings,
}) => {
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sr No.</TableCell>
              <TableCell>LR No.</TableCell>
              <TableCell>Party Name</TableCell>
              <TableCell>Drop Location</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Tax</TableCell>
              <TableCell align="right">To Pay</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Operator</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Pricing</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!bookings || bookings?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} align="center">
                  No luggage bookings added
                </TableCell>
              </TableRow>
            ) : (
              bookings?.map((booking, index) => {
                return (
                  <TableRow
                    key={booking.id}
                    sx={{
                      backgroundColor: "inherit",
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{booking.lrNo}</TableCell>
                    <TableCell>{booking.partyName}</TableCell>
                    <TableCell>{booking.dropLocation || "-"}</TableCell>
                    <TableCell>{booking.quantity}</TableCell>
                    <TableCell>₹{booking.rate}</TableCell>
                    <TableCell>{booking.tax}</TableCell>
                    <TableCell align="right">
                      {booking.toPay ? `₹${booking.toPay}` : "-"}
                    </TableCell>
                    <TableCell align="right">
                      {booking.paid ? `₹${booking.paid}` : "-"}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: "inherit",
                        position: "relative",
                      }}
                    >
                      ₹{booking.amount}
                    </TableCell>
                    <TableCell>{booking.luggageOperator || "-"}</TableCell>
                    <TableCell>
                      {booking.luggageOperatorContactNumber || "-"}
                    </TableCell>
                    <TableCell>
                      {booking.luggageOperatorPricingType &&
                      booking.luggageOperatorPricingValue
                        ? `${booking.luggageOperatorPricingType}: ₹${booking.luggageOperatorPricingValue}`
                        : "-"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            {bookings && bookings?.length > 0 && (
              <TableRow>
                <TableCell
                  colSpan={12}
                  align="right"
                  sx={{ fontWeight: "bold" }}
                >
                  Total Amount:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  ₹
                  {Number(
                    bookings.reduce((sum, booking) => sum + booking.amount, 0)
                  )}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

interface ExpenseSectionProps {
  title: string;
  expenses: any[];
  type: string;
  renderExpenseDetails: (expense: any) => React.ReactNode;
}

const ExpenseSection: React.FC<ExpenseSectionProps> = ({
  title,
  expenses,
  type,
  renderExpenseDetails,
}) => {
  const filteredExpenses =
    type === "OTHER"
      ? expenses.filter(
          (expense) =>
            !["DRIVER", "CLEANER", "FUEL", "TOLL", "PARKING"].includes(
              expense.type
            )
        )
      : expenses.filter((expense) => expense.type === type);

  if (filteredExpenses.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: "bold", color: "primary.main", mb: 2 }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {filteredExpenses.map((expense, index) => (
          <Paper key={index} sx={{ p: 2 }}>
            {renderExpenseDetails(expense)}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Payments
              </Typography>
              {expense.payments.map((payment: any, pIndex: any) => (
                <Paper
                  key={pIndex}
                  sx={{
                    p: 1,
                    mb: 1,
                    bgcolor: "#f5f5f5",
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Typography variant="body2">
                        {payment.paymentType}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body2">₹{payment.amount}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Method
                      </Typography>
                      <Typography variant="body2">
                        {payment.paymentMethod === "Advance_Cash"
                          ? "Advance Cash"
                          : payment.paymentMethod}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2">
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

const TripDetails = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [trip, setTrip] = useState<TripDetails | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [tripIncomes, setTripIncomes] = useState<TripIncomes>({
    incomes: [],
    busIncomes: [],
    luggageIncomes: [],
    summary: {
      totalIncome: 0,
      totalAdvancePayment: 0,
      totalCommission: 0,
      busBookingCount: 0,
      luggageBookingCount: 0,
      startLuggageBookingCount: 0,
      returnLuggageBookingCount: 0,
      byType: {},
      byPaymentMethod: {},
      balanceAmount: 0,
      totalBookings: 0,
    },
  });
  const [tripExpenses, setTripExpenses] = useState<Expenses>({
    expenses: [],
    summary: {
      totalExpense: 0,
      totalPaid: 0,
      balanceAmount: 0,
      byType: {},
    },
  });
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const [selectedIncomeFiles, setSelectedIncomeFiles] = useState<File[]>([]);
  const [selectedExpenseFiles, setSelectedExpenseFiles] = useState<File[]>([]);
  const [uploadedIncomeDocuments, setUploadedIncomeDocuments] = useState<
    UploadedFile[]
  >([]);
  const [uploadedExpenseDocuments, setUploadedExpenseDocuments] = useState<
    UploadedFile[]
  >([]);

  const handleUpload = async (type: "income" | "expense") => {
    const filesToUpload =
      type === "income" ? selectedIncomeFiles : selectedExpenseFiles;
    if (filesToUpload.length === 0) return;

    setIsLoading(true);
    try {
      const uploadPromises = filesToUpload.map((file) =>
        uploadFile({
          file,
          documentType: type === "income" ? "trip-income" : "trip-expense",
          documentTypeId: tripId || "",
          documentContext: "trip",
          description: `Trip ${type} proof`,
          companyId: companyId,
        })
      );

      await Promise.all(uploadPromises);
      await fetchAllDocuments({
        tripId: tripId || "",
        setUploadedIncomeDocuments,
        setUploadedExpenseDocuments,
      });

      // Clear selected files after successful upload
      if (type === "income") {
        setSelectedIncomeFiles([]);
      } else {
        setSelectedExpenseFiles([]);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const fetchTripDetails = async () => {
    try {
      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}trips/${tripId}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        setTrip(response.data.data.trip);
        setTripIncomes(
          response.data.data.tripIncomes || {
            incomes: [],
            summary: {
              totalIncome: 0,
              totalAdvancePayment: 0,
              totalCommission: 0,
              busBookingCount: 0,
              luggageBookingCount: 0,
              byType: {},
              byPaymentMethod: {},
              balanceAmount: 0,
              totalBookings: 0,
            },
          }
        );
        setTripExpenses(
          response.data.data.tripExpenses || {
            expenses: [],
            summary: {
              totalExpense: 0,
              totalPaid: 0,
              balanceAmount: 0,
              byType: {},
            },
          }
        );
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user && tripId) {
      fetchTripDetails();
      fetchAllDocuments({
        tripId: tripId || "",
        setUploadedIncomeDocuments,
        setUploadedExpenseDocuments,
      });
    }
  }, [user, authLoading, tripId]);

  const handleBack = () => {
    navigate(-1);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ONGOING":
        return "warning";
      case "COMPLETED":
        return "success";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (authLoading || isLoading) {
    return <Loading message="Loading trip details..." fullScreen={true} />;
  }

  if (!trip) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" color="error">
          Trip not found
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            onClick={() => navigate("/dashboard")}
            sx={{ cursor: "pointer" }}
          >
            Dashboard
          </Link>
          <Link
            color="inherit"
            onClick={() => navigate("/trips")}
            sx={{ cursor: "pointer" }}
          >
            Trips
          </Link>
          <Typography color="text.primary">Trip Details</Typography>
        </Breadcrumbs>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Trip Details
          </Typography>
          <Chip
            label={trip.status}
            color={getStatusColor(trip.status) as any}
            size="medium"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Vehicle Details */}
        {trip.vehicle && <BasicVehicleDetailsView vehicle={trip.vehicle} />}

        {/* Trip Information Card */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <DirectionsBus sx={{ fontSize: 40, color: "#000" }} />
            <Typography variant="h6">Trip Information</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Trip Type
              </Typography>
              <Typography variant="body1">{trip.tripType}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Route
              </Typography>
              <Typography variant="body1">
                {trip.fromLocation} → {trip.toLocation}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Journey Start
              </Typography>
              <Typography variant="body1">
                {new Date(trip.journeyStartDate).toLocaleDateString("en-GB")}
              </Typography>
            </Grid>
            {trip.tripType === "Round" && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Journey Return
                </Typography>
                <Typography variant="body1">
                  {new Date(trip.journeyReturnDate).toLocaleDateString("en-GB")}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Odometer Start
              </Typography>
              <Typography variant="body1">
                {trip.vehicleOdometerStart || "N/A"} km
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">
                Odometer End
              </Typography>
              <Typography variant="body1">
                {trip.vehicleOdometerEnd || "N/A"} km
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Income and Expenses Tabs */}
        <Box
          sx={{
            mb: 4,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            width: "100%",
          }}
        >
          <Grid item xs={12}>
            <Paper sx={{ width: "100%" }}>
              <Grid container>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderBottom:
                        selectedTab === 0 ? "2px solid #1976d2" : "none",
                      bgcolor:
                        selectedTab === 0
                          ? "transparent"
                          : "rgba(25, 118, 210, 0.08)",
                      "&:hover": {
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                    onClick={() => setSelectedTab(0)}
                  >
                    <Typography
                      variant="h6"
                      color={
                        selectedTab === 0 ? "text.primary" : "text.secondary"
                      }
                    >
                      INCOME
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Income: ₹{tripIncomes.summary.totalIncome}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderBottom:
                        selectedTab === 1 ? "2px solid #1976d2" : "none",
                      bgcolor:
                        selectedTab === 1
                          ? "transparent"
                          : "rgba(25, 118, 210, 0.08)",
                      "&:hover": {
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                    onClick={() => setSelectedTab(1)}
                  >
                    <Typography
                      variant="h6"
                      align="right"
                      color={
                        selectedTab === 1 ? "text.primary" : "text.secondary"
                      }
                    >
                      EXPENSE
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="right"
                    >
                      Total Expense: ₹{tripExpenses.summary.totalExpense}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ p: 3, borderTop: "1px solid rgba(0, 0, 0, 0.12)" }}>
                {selectedTab === 0 && (
                  <>
                    {tripIncomes?.busIncomes?.length > 0 && (
                      <Box>
                        {/* Income Details */}
                        {tripIncomes?.busIncomes?.length === 0 ? (
                          <Typography
                            color="text.secondary"
                            align="center"
                            sx={{ py: 4 }}
                          >
                            No bus booking records found
                          </Typography>
                        ) : (
                          tripIncomes?.busIncomes?.map((income, idx) => (
                            <Paper sx={{ p: 2, mb: 3 }} key={idx}>
                              <Typography
                                variant="subtitle1"
                                color="primary"
                                sx={{ fontWeight: "medium", mb: 2 }}
                              >
                                {income.journeyType === "START"
                                  ? `Start Journey - ${trip?.fromLocation} to ${trip?.toLocation}`
                                  : `Return Journey - ${trip?.toLocation} to ${trip?.fromLocation}`}
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Booking Type
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.bookingMode || "-"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Operator Type
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.operatorType || "-"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Bus Operator Name
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.operatorName || "-"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Operator Contact Name
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.operatorContactName || "-"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Contact Number
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.operatorContactNumber || "-"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    {income.bookingMode === "CONTRACT"
                                      ? "Contract Amount"
                                      : "Commission Amount"}
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.contractAmount || "-"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Remark
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.remark || "-"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Received Advance Payment
                                  </Typography>
                                  <Typography variant="body1">
                                    {income.receivedAdvancePayment
                                      ? "Yes"
                                      : "No"}
                                  </Typography>
                                </Grid>
                              </Grid>
                              {income.paymentDetails &&
                                income.paymentDetails.length > 0 && (
                                  <Box
                                    sx={{
                                      mt: 3,
                                      bgcolor: "#f5f5f5",
                                      p: 2,
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                    >
                                      Advance Payment Details
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.secondary"
                                        >
                                          Payment Received From
                                        </Typography>
                                        <Typography variant="body1">
                                          {income.paymentDetails[0]
                                            .paymentReceivedFrom || "-"}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.secondary"
                                        >
                                          Payment Mode
                                        </Typography>
                                        <Typography variant="body1">
                                          {income.paymentDetails[0]
                                            .paymentMode || "-"}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.secondary"
                                        >
                                          Payment Amount
                                        </Typography>
                                        <Typography variant="body1">
                                          {income.paymentDetails[0]
                                            .paymentAmount || "-"}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.secondary"
                                        >
                                          Payment Date
                                        </Typography>
                                        <Typography variant="body1">
                                          {income.paymentDetails[0]
                                            .paymentDate || "-"}
                                        </Typography>
                                      </Grid>

                                      <Grid item xs={6}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.secondary"
                                        >
                                          Payment Location
                                        </Typography>
                                        <Typography variant="body1">
                                          {income.paymentDetails[0]
                                            .paymentLocation || "-"}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.secondary"
                                        >
                                          Remarks
                                        </Typography>
                                        <Typography variant="body1">
                                          {income.paymentDetails[0].remarks ||
                                            "-"}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )}
                              {income.commissionBookings?.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 3,
                                    bgcolor: "#f5f5f5",
                                    p: 2,
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                  >
                                    Commission Bookings
                                  </Typography>
                                  <Grid container spacing={2}>
                                    Pending
                                  </Grid>
                                </Box>
                              )}
                              {/* <Box
                              sx={{
                                mt: 3,
                                bgcolor: "#f5f5f5",
                                p: 2,
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight="bold">
                                Balance Remaining
                              </Typography>
                              <Typography variant="body1">
                                {income.balanceRemaining || "0"}
                              </Typography>
                            </Box> */}
                            </Paper>
                          ))
                        )}
                      </Box>
                    )}
                    {tripIncomes?.luggageIncomes && (
                      <Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Luggage sx={{ fontSize: 40, color: "#000" }} />
                          Luggage Booking
                        </Typography>
                        {tripIncomes?.luggageIncomes?.length === 0 ? (
                          <Typography
                            color="text.secondary"
                            align="center"
                            sx={{ py: 4 }}
                          >
                            No luggage booking records found
                          </Typography>
                        ) : (
                          <>
                            <LuggageJourneySection
                              journeyType="START"
                              incomes={tripIncomes.luggageIncomes}
                              fromLocation={trip?.fromLocation || ""}
                              toLocation={trip?.toLocation || ""}
                            />
                            {tripIncomes?.summary?.busBookingCount > 1 && (
                              <LuggageJourneySection
                                journeyType="RETURN"
                                incomes={tripIncomes.luggageIncomes}
                                fromLocation={trip?.fromLocation || ""}
                                toLocation={trip?.toLocation || ""}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    )}
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <FileUploadSection
                        title="Income Proofs"
                        selectedFiles={selectedIncomeFiles}
                        uploadedFiles={uploadedIncomeDocuments}
                        onFileSelect={(files) =>
                          setSelectedIncomeFiles((prev) => [...prev, ...files])
                        }
                        onFileRemove={(index) =>
                          setSelectedIncomeFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        onUpload={() => handleUpload("income")}
                        onPreview={handlePreview}
                        isLoading={isLoading}
                        buttonText="Select Income Proofs"
                        uploadButtonText="Upload Selected Income Proofs"
                      />
                    </Paper>
                  </>
                )}

                {selectedTab === 1 && (
                  <Box>
                    {tripExpenses.expenses.length === 0 ? (
                      <Typography
                        color="text.secondary"
                        align="center"
                        sx={{ py: 4 }}
                      >
                        No expense records found
                      </Typography>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">Expenses</Typography>
                          <Box
                            display="flex"
                            gap={2}
                            flexDirection="row"
                            alignItems="center"
                          >
                            {trip?.advancePayment && (
                              <Typography
                                variant="body1"
                                color="primary"
                                fontWeight="bold"
                              >
                                Advance Payment: ₹{trip?.advancePayment}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <ExpenseSection
                          title="Driver Expenses"
                          expenses={tripExpenses.expenses}
                          type="DRIVER"
                          renderExpenseDetails={(expense) => (
                            <>
                              <Typography
                                variant="subtitle1"
                                color="primary"
                                sx={{ mb: 2 }}
                              >
                                Driver{" "}
                                {tripExpenses.expenses
                                  .filter((e) => e.type === "DRIVER")
                                  .indexOf(expense) + 1}{" "}
                                Bhatta
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Driver Name
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.paidTo}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Contact Number
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.contactNumber}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    License Number
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.receipientIdNumber}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Amount
                                  </Typography>
                                  <Typography variant="body1">
                                    ₹{expense.amount}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </>
                          )}
                        />

                        <ExpenseSection
                          title="Cleaner Expenses"
                          expenses={tripExpenses.expenses}
                          type="CLEANER"
                          renderExpenseDetails={(expense) => (
                            <>
                              <Typography
                                variant="subtitle1"
                                color="primary"
                                sx={{ mb: 2 }}
                              >
                                Cleaner{" "}
                                {tripExpenses.expenses
                                  .filter((e) => e.type === "CLEANER")
                                  .indexOf(expense) + 1}{" "}
                                Bhatta
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Cleaner Name
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.paidTo}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Contact Number
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.contactNumber}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Amount
                                  </Typography>
                                  <Typography variant="body1">
                                    ₹{expense.amount}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </>
                          )}
                        />

                        <ExpenseSection
                          title="Diesel Expenses"
                          expenses={tripExpenses.expenses}
                          type="FUEL"
                          renderExpenseDetails={(expense) => (
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Fuel Location
                                </Typography>
                                <Typography variant="body1">
                                  {expense.fuelLocation}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Fuel Provider
                                </Typography>
                                <Typography variant="body1">
                                  {expense.fuelProvider}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Quantity
                                </Typography>
                                <Typography variant="body1">
                                  {expense.quantity} L
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Odometer Reading
                                </Typography>
                                <Typography variant="body1">
                                  {expense.odometerReading} km
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Amount
                                </Typography>
                                <Typography variant="body1">
                                  ₹{expense.amount}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                        />

                        <ExpenseSection
                          title="Toll Expenses"
                          expenses={tripExpenses.expenses}
                          type="TOLL"
                          renderExpenseDetails={(expense) => (
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Location
                                </Typography>
                                <Typography variant="body1">
                                  {expense.location}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Amount
                                </Typography>
                                <Typography variant="body1">
                                  ₹{expense.amount}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                        />

                        <ExpenseSection
                          title="Parking Expenses"
                          expenses={tripExpenses.expenses}
                          type="PARKING"
                          renderExpenseDetails={(expense) => (
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Location
                                </Typography>
                                <Typography variant="body1">
                                  {expense.location}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Amount
                                </Typography>
                                <Typography variant="body1">
                                  ₹{expense.amount}
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                        />

                        <ExpenseSection
                          title="Other Expenses"
                          expenses={tripExpenses.expenses}
                          type="OTHER"
                          renderExpenseDetails={(expense) => (
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Type
                                </Typography>
                                <Typography variant="body1">
                                  {expense.type}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Amount
                                </Typography>
                                <Typography variant="body1">
                                  ₹{expense.amount}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  {expense.type === "Challan"
                                    ? "Issuing Authority"
                                    : "Paid To"}
                                </Typography>
                                <Typography variant="body1">
                                  {expense.paidTo || expense?.mechanicName}
                                </Typography>
                              </Grid>
                              {expense.contactNumber && (
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Contact Number
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.contactNumber}
                                  </Typography>
                                </Grid>
                              )}
                              {expense.receipientIdNumber && (
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    {expense.type === "Challan"
                                      ? "Challan Number"
                                      : "ID Number"}
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.receipientIdNumber}
                                  </Typography>
                                </Grid>
                              )}
                              {expense.location && (
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Location
                                  </Typography>
                                  <Typography variant="body1">
                                    {expense.location}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          )}
                        />
                      </>
                    )}
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <FileUploadSection
                        title="Expense Proofs"
                        selectedFiles={selectedExpenseFiles}
                        uploadedFiles={uploadedExpenseDocuments}
                        onFileSelect={(files) =>
                          setSelectedExpenseFiles((prev) => [...prev, ...files])
                        }
                        onFileRemove={(index) =>
                          setSelectedExpenseFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        onUpload={() => handleUpload("expense")}
                        onPreview={handlePreview}
                        isLoading={isLoading}
                        buttonText="Select Expense Proofs"
                        uploadButtonText="Upload Selected Expense Proofs"
                      />
                    </Paper>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Box>
      </Grid>
    </Container>
  );
};

export default TripDetails;
